import * as Sentry from "@sentry/react";

/**
 * Sentry Configuration
 *
 * This module initializes Sentry error tracking for the admin dashboard.
 * Sentry is enabled for staging and production only, each reporting to its
 * own project, so local development never sends events.
 */

const SENTRY_DSN_BY_ENVIRONMENT: Record<string, string> = {
  production:
    "https://3b5c9146488c649e5e262d710c9b36b1@o4505820919169024.ingest.us.sentry.io/4510203776008192",
  staging:
    "https://292bb944fc543a78b2018be0d7755d0b@o4505820919169024.ingest.us.sentry.io/4511909555142656",
};

/**
 * Determines if Sentry should be enabled based on the environment.
 *
 * Sentry is enabled only when:
 * 1. VITE_SENTRY_ENVIRONMENT is explicitly set to "production" or "staging"
 *
 * This ensures errors are only sent to Sentry in deployed environments,
 * not in local development.
 */
export const isSentryEnabled = (): boolean => {
  const sentryEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  return sentryEnv === "production" || sentryEnv === "staging";
};

/**
 * Initializes Sentry SDK with configuration optimized for React applications.
 */
export const initSentry = (): void => {
  if (!isSentryEnabled()) {
    console.log("Sentry is disabled for this environment");
    return;
  }

  const sentryEnv = import.meta.env.VITE_SENTRY_ENVIRONMENT;

  Sentry.init({
    dsn: SENTRY_DSN_BY_ENVIRONMENT[sentryEnv],

    // Set the environment name
    environment: sentryEnv,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production to a lower value (e.g., 0.1 = 10%)
    tracesSampleRate: 0.1,

    // Capture 10% of all sessions for replay
    replaysSessionSampleRate: 0.1,

    // Capture 100% of sessions with errors for replay
    replaysOnErrorSampleRate: 1.0,

    // Before sending events, you can modify or filter them
    beforeSend(event, hint) {
      // Log errors to console in non-production for debugging
      if (import.meta.env.DEV) {
        console.error("Sentry event:", event, hint);
      }
      return event;
    },

    // Filter out certain errors that are not useful
    ignoreErrors: [
      // Browser extensions
      /Extension context invalidated/,
      // Network errors
      /NetworkError/,
      /Failed to fetch/,
      // Random plugins/extensions
      /Non-Error promise rejection captured/,
    ],
  });

  console.log("Sentry initialized successfully");
};
